import React, { FC, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import {
  CustomModalDto,
  QuestionConditionalModalDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { Box, Button, IconButton, Typography } from "@components/base";
import { useUserStore } from "@store/user";
import { useGetInTouch } from "@api";

interface GetInTouchModal {
  open: boolean;
  onClose?: () => void;
  onConfirmed?: () => void;
  data: QuestionConditionalModalDto | CustomModalDto | undefined;
  isHardPaywall?: boolean;
}

// TODO add default styles for modal in theme
const GetInTouchModal: FC<GetInTouchModal> = ({
  open,
  onClose,
  onConfirmed,
  data,
  isHardPaywall = false,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useUserStore();
  const { t } = useTranslation();

  const { sendGetInTouchEmailAsync } = useGetInTouch(user);

  if (!data) return null;

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await sendGetInTouchEmailAsync();
      setSubmitted(true);
    } catch (error) {
      console.error("Get in touch failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    setSubmitted(false);
    onConfirmed?.();
    if (onClose) {
      onClose();
    }
  };

  const title = submitted
    ? t("modal.getInTouch.success.title")
    : (data?.title ?? "");

  const description = submitted ? (
    <Typography>{t("modal.getInTouch.success.body")}</Typography>
  ) : (
    <Typography>{data?.body ?? ""}</Typography>
  );

  const buttonText = submitted
    ? t("modal.getInTouch.success.buttonLabelConfirm")
    : (data?.buttonLabelConfirm ?? "");

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (isHardPaywall) {
          return;
        }

        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
    >
      <Box
        p={4}
        maxWidth={500}
        mx="auto"
        my="20vh"
        borderRadius={2}
        boxShadow={3}
        bgcolor="background.paper"
        sx={{ position: "relative" }}
      >
        {!submitted && !isHardPaywall && (
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
        <Box textAlign="center" display="flex" flexDirection="column" gap={3}>
          <Typography component="div" variant="h6">
            {title}
          </Typography>
          <Typography
            textAlign="center"
            component="div"
            variant="body1"
            color="textSecondary"
          >
            {description}
          </Typography>
          <Button
            size="medium"
            onClick={submitted ? handleClose : handleSubmit}
            isLoading={isLoading}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default GetInTouchModal;
